// src/components/routes/ProtectedVendorRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, Timestamp } from "firebase/firestore";

function LoadingVendorAccess() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        color: "#6b7280",
      }}
    >
      Chargement de votre espace vendeur…
    </div>
  );
}

function toMillisSafe(value) {
  if (!value) return null;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    const t = d.getTime();
    return Number.isNaN(t) ? null : t;
  }

  return null;
}

export default function ProtectedVendorRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [fbUser, setFbUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setFbUser(user);

      if (!user) {
        setUserDoc(null);
        setLoading(false);
        return;
      }

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setUserDoc({ id: snap.id, ...snap.data() });
        } else {
          setUserDoc(null);
        }
      } catch (err) {
        console.error("[ProtectedVendorRoute] getDoc users:", err);
        setUserDoc(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  if (loading) {
    return <LoadingVendorAccess />;
  }

  if (!fbUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const role = String(userDoc?.role || "").toLowerCase();
  const isVendor = userDoc?.isVendor === true || role === "vendor";
  const isAdmin =
    userDoc?.isAdmin === true ||
    role === "admin" ||
    role === "superadmin";

  if (!isVendor && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // ✅ Admin = accès total
  if (isAdmin) {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, { userDoc });
    }
    return children;
  }

  // =========================
  // 🔐 LOGIQUE VENDEUR
  // =========================

  const packId = String(
    userDoc?.packId || userDoc?.subscriptionPlan || "standard"
  ).toLowerCase();

  const paymentStatus = String(userDoc?.paymentStatus || "").toLowerCase();
  const passwordSetupStatus = String(
    userDoc?.passwordSetupStatus || ""
  ).toLowerCase();

  const subscriptionStatus = String(
    userDoc?.subscriptionStatus || ""
  ).toLowerCase();

  const accessStatus = String(userDoc?.accessStatus || "").toLowerCase();

  const onboardingToken = userDoc?.onboardingToken || "";

  const validUntil =
    userDoc?.subscriptionValidUntil ||
    userDoc?.subscriptionEndsAt ||
    userDoc?.currentPeriodEnd ||
    userDoc?.nextBillingDate ||
    null;

  const nowMillis = Timestamp.now().toMillis();
  const validUntilMillis = toMillisSafe(validUntil);

  const paidPacks = ["standard_plus", "pro_semestre", "pro"];
  const isPaidPack = paidPacks.includes(packId);

  const onboardingUrl = onboardingToken
    ? `/vendor/onboarding?token=${encodeURIComponent(onboardingToken)}`
    : "/vendor/abonnement";

  // =========================
  // ✅ FALLBACK ANCIENS VENDEURS
  // =========================
  // Si ancien vendeur sans nouveaux champs,
  // on considère qu'il est actif pour ne pas le bloquer.
  const isLegacyVendor =
    isVendor &&
    !userDoc?.accessStatus &&
    !userDoc?.passwordSetupStatus &&
    !userDoc?.paymentStatus;

  if (isLegacyVendor) {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, { userDoc });
    }
    return children;
  }

  // =========================
  // ✅ NOUVELLE LOGIQUE CENTRALISÉE
  // =========================

  // Si accessStatus existe et vaut active, on laisse passer
  // mais on garde quand même une sécurité d'abonnement pour les packs payants.
  if (accessStatus === "active") {
    if (packId === "standard") {
      if (React.isValidElement(children)) {
        return React.cloneElement(children, { userDoc });
      }
      return children;
    }

    const paidOk = paymentStatus === "paid";
    const subOk =
      subscriptionStatus === "active" ||
      subscriptionStatus === "paid" ||
      subscriptionStatus === "trial";

    const dateOk =
      validUntilMillis === null ? true : validUntilMillis > nowMillis;

    if (paidOk && subOk && dateOk) {
      if (React.isValidElement(children)) {
        return React.cloneElement(children, { userDoc });
      }
      return children;
    }

    return (
      <Navigate
        to="/vendor/abonnement"
        replace
        state={{ from: location }}
      />
    );
  }

  // 1) Pack payant non payé
  if (isPaidPack && paymentStatus !== "paid") {
    return <Navigate to={onboardingUrl} replace state={{ from: location }} />;
  }

  // 2) Mot de passe non défini
  if (passwordSetupStatus && passwordSetupStatus !== "completed") {
    return <Navigate to={onboardingUrl} replace state={{ from: location }} />;
  }

  // 3) Si pack gratuit et mot de passe ok
  if (!isPaidPack) {
    const passwordOk =
      !passwordSetupStatus || passwordSetupStatus === "completed";

    if (passwordOk) {
      if (React.isValidElement(children)) {
        return React.cloneElement(children, { userDoc });
      }
      return children;
    }

    return <Navigate to={onboardingUrl} replace state={{ from: location }} />;
  }

  // 4) Packs payants : abonnement actif
  const hasValidDate =
    validUntilMillis === null ? false : validUntilMillis > nowMillis;

  const hasActiveSubscription =
    paymentStatus === "paid" &&
    (subscriptionStatus === "active" ||
      subscriptionStatus === "paid" ||
      subscriptionStatus === "trial") &&
    hasValidDate;

  if (!hasActiveSubscription) {
    return (
      <Navigate
        to="/vendor/abonnement"
        replace
        state={{ from: location }}
      />
    );
  }

  // ✅ Accès OK
  if (React.isValidElement(children)) {
    return React.cloneElement(children, { userDoc });
  }

  return children;
}